import React, { useEffect, useMemo, useState } from "react";
import Sidebar from "../components/shared/Sidebar";
import Navbar from "../components/shared/Navbar";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { UserPlus, Shield, Crown, UserCheck, Users, Loader2, Trash2, Pencil } from "lucide-react";
import { toast } from "sonner";

const AVATAR_COLORS = [
  "from-emerald-500 to-green-600", "from-blue-500 to-indigo-600", "from-purple-500 to-pink-600",
  "from-orange-500 to-red-600", "from-cyan-500 to-blue-600", "from-pink-500 to-rose-600",
];

function Avatar({ name, idx }) {
  const color = AVATAR_COLORS[idx % AVATAR_COLORS.length];
  return (
    <div className={`w-11 h-11 rounded-full bg-gradient-to-br ${color} flex items-center justify-center text-sm font-bold text-white shrink-0`}>
      {(name || "?").slice(0, 2).toUpperCase()}
    </div>
  );
}

export default function Team() {
  const { user } = useAuth();
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "agent" });
  const [busy, setBusy] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editBusy, setEditBusy] = useState(false);
  const [resetBusy, setResetBusy] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  const [editForm, setEditForm] = useState({ name: "", email: "", role: "agent" });
  const [temporaryPassword, setTemporaryPassword] = useState("");

  const currentUserId = user?.id;
  const isAdminRole = (role) => ["owner", "admin", "super_admin"].includes(role);
  const currentAdminCount = useMemo(
    () => members.filter((member) => isAdminRole(member.role)).length,
    [members],
  );

  const reload = () => {
    setLoading(true);
    api.get("/team")
      .then((r) => setMembers(r.data))
      .finally(() => setLoading(false));
  };

  useEffect(() => { reload(); }, []);

  const patchMember = async (memberId, payload) => {
    try {
      return await api.patch(`/users/${memberId}`, payload);
    } catch (error) {
      if (error?.response?.status === 404) {
        return api.patch(`/team/${memberId}`, payload);
      }
      throw error;
    }
  };

  const create = async () => {
    if (!form.name || !form.email || !form.password) return;
    setBusy(true);
    try {
      await api.post("/team", form);
      toast.success("Membro adicionado");
      setOpen(false);
      setForm({ name: "", email: "", password: "", role: "agent" });
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao adicionar");
    } finally {
      setBusy(false);
    }
  };

  const toggle = async (member) => {
    try {
      const { data } = await api.patch(`/team/${member.id}`, { active: !member.active });
      setMembers((prev) => prev.map((current) => (current.id === member.id ? data : current)));
    } catch {
      toast.error("Erro");
    }
  };

  const openEditModal = (member) => {
    setEditingMember(member);
    setEditForm({
      name: member.name || "",
      email: member.email || "",
      role: member.role || "agent",
    });
    setTemporaryPassword("");
    setEditOpen(true);
  };

  const saveEdit = async () => {
    if (!editingMember) return;

    const payload = {
      name: editForm.name.trim(),
      email: editForm.email.trim(),
    };
    if (["admin", "agent"].includes(editForm.role)) {
      payload.role = editForm.role;
    }

    if (!payload.name || !payload.email) {
      toast.error("Preencha nome e e-mail");
      return;
    }

    if (editingMember.id === currentUserId && payload.role === "agent") {
      toast.error("Você não pode rebaixar a si mesmo");
      return;
    }

    const isDowngrade = isAdminRole(editingMember.role) && payload.role === "agent";
    if (isDowngrade) {
      if (currentAdminCount <= 1) {
        toast.error("Deve existir pelo menos um Admin na equipe");
        return;
      }
      if (!window.confirm("Tem certeza que deseja remover os privilégios de Admin deste usuário?")) return;
    }

    setEditBusy(true);
    try {
      const { data } = await patchMember(editingMember.id, payload);
      setMembers((prev) => prev.map((member) => (member.id === editingMember.id ? data : member)));
      setEditingMember(data);
      setEditOpen(false);
      toast.success("Membro atualizado");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao salvar alterações");
    } finally {
      setEditBusy(false);
    }
  };

  const toggleRoleQuick = async (member) => {
    const nextRole = isAdminRole(member.role) ? "agent" : "admin";

    if (member.id === currentUserId && nextRole === "agent") {
      toast.error("Você não pode rebaixar a si mesmo");
      return;
    }

    if (nextRole === "agent") {
      if (currentAdminCount <= 1) {
        toast.error("Deve existir pelo menos um Admin na equipe");
        return;
      }
      if (!window.confirm("Tem certeza que deseja remover os privilégios de Admin deste usuário?")) return;
    } else if (!window.confirm(`Promover ${member.name} para Admin?`)) {
      return;
    }

    try {
      const { data } = await patchMember(member.id, { role: nextRole });
      setMembers((prev) => prev.map((current) => (current.id === member.id ? data : current)));
      if (editingMember?.id === member.id) {
        setEditingMember(data);
        setEditForm((prev) => ({ ...prev, role: data.role }));
      }
      toast.success(nextRole === "admin" ? "Usuário promovido a Admin" : "Usuário rebaixado para Agente");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao alterar cargo");
    }
  };

  const remove = async (member) => {
    if (!window.confirm(`Remover ${member.name} do time?`)) return;
    try {
      await api.delete(`/team/${member.id}`);
      setMembers((prev) => prev.filter((current) => current.id !== member.id));
      toast.info("Removido");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro");
    }
  };

  const resetPassword = async () => {
    if (!editingMember) return;
    setResetBusy(true);
    try {
      const { data } = await api.patch(`/users/${editingMember.id}/reset-password`, {
        password: temporaryPassword.trim() || undefined,
      });
      setTemporaryPassword(data?.temporary_password || "");
      toast.success(`Senha redefinida: ${data?.temporary_password || "gerada com sucesso"}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao resetar senha");
    } finally {
      setResetBusy(false);
    }
  };

  const active = members.filter((member) => member.active).length;

  return (
    <div className="flex h-screen bg-slate-950 overflow-hidden">
      <Sidebar />
      <main className="flex-1 flex flex-col min-w-0 overflow-y-auto">
        <Navbar
          title="Equipe"
          subtitle="Membros e distribuição round-robin de leads"
          actions={(
            <Button
              data-testid="add-member-btn"
              onClick={() => setOpen(true)}
              className="bg-[#25D366] hover:bg-[#1EAC52] text-slate-950 font-semibold"
            >
              <UserPlus className="w-4 h-4 mr-1.5" /> Adicionar membro
            </Button>
          )}
        />

        <div className="px-6 py-6 max-w-5xl space-y-5">
          <div className="grid grid-cols-3 gap-3">
            {[
              { icon: Users, label: "Total", value: members.length, color: "text-slate-200 bg-slate-700/40" },
              { icon: UserCheck, label: "Ativos", value: active, color: "text-[#25D366] bg-[#25D366]/10" },
              { icon: Shield, label: "Round-Robin", value: active > 0 ? `Rotação: ${active}` : "Pausado", color: "text-purple-400 bg-purple-500/10" },
            ].map((card, i) => (
              <div key={i} className="bg-slate-900/60 border border-slate-800 rounded-xl p-4 flex items-center gap-3">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${card.color}`}>
                  <card.icon className="w-5 h-5" />
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{card.label}</div>
                  <div className="text-lg font-bold text-slate-50" style={{ fontFamily: "Outfit" }}>{card.value}</div>
                </div>
              </div>
            ))}
          </div>

          <div className="bg-slate-900/60 border border-slate-800 rounded-xl overflow-hidden">
            <div className="px-5 py-3 border-b border-slate-800 flex items-center justify-between">
              <h3 className="text-sm font-bold text-slate-100" style={{ fontFamily: "Outfit" }}>
                Membros ({members.length})
              </h3>
              <p className="text-[11px] text-slate-500">Novos leads via webhook são atribuídos automaticamente em ordem</p>
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-10">
                <Loader2 className="w-6 h-6 text-[#25D366] animate-spin" />
              </div>
            ) : (
              <div className="divide-y divide-slate-800">
                {members.map((member, i) => (
                  <div key={member.id} data-testid={`member-row-${member.id}`} className="flex items-center gap-3 px-5 py-3 hover:bg-slate-800/30 transition">
                    <Avatar name={member.name} idx={i} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <div className="text-sm font-semibold text-slate-100">{member.name}</div>
                        {isAdminRole(member.role) ? (
                          <span className="inline-flex items-center gap-1 text-[9px] font-bold text-[#25D366] bg-[#25D366]/10 border border-[#25D366]/30 px-1.5 py-0.5 rounded">
                            <Crown className="w-2.5 h-2.5" /> {member.role === "owner" ? "DONO" : "ADMIN"}
                          </span>
                        ) : (
                          <span className="text-[9px] font-bold text-sky-300 bg-sky-500/10 border border-sky-500/20 px-1.5 py-0.5 rounded">
                            AGENTE
                          </span>
                        )}
                        {!member.active && (
                          <span className="text-[9px] font-bold text-slate-500 bg-slate-800 px-1.5 py-0.5 rounded">PAUSADO</span>
                        )}
                      </div>
                      <div className="text-xs text-slate-400">{member.email}</div>
                    </div>

                    <div className="flex items-center gap-1 flex-wrap justify-end">
                      <button
                        onClick={() => openEditModal(member)}
                        className="p-1.5 text-slate-400 hover:text-sky-300 hover:bg-sky-500/10 rounded-md transition"
                        title="Editar membro"
                      >
                        <Pencil className="w-3.5 h-3.5" />
                      </button>
                      <button
                        data-testid={`member-toggle-${member.id}`}
                        onClick={() => toggle(member)}
                        className={`text-xs font-semibold px-2.5 py-1 rounded-md transition border ${
                          member.active
                            ? "text-slate-300 border-slate-700 hover:bg-slate-800"
                            : "text-[#25D366] border-[#25D366]/40 bg-[#25D366]/10"
                        }`}
                      >
                        {member.active ? "Pausar" : "Ativar"}
                      </button>
                      <button
                        onClick={() => toggleRoleQuick(member)}
                        className={`text-xs font-semibold px-2.5 py-1 rounded-md transition border ${
                          isAdminRole(member.role)
                            ? "border-slate-700 text-slate-300 hover:bg-slate-800"
                            : "border-amber-500/30 text-amber-300 hover:bg-amber-500/10"
                        }`}
                      >
                        {isAdminRole(member.role) ? "Rebaixar para Agente" : "Promover a Admin"}
                      </button>
                      <button
                        data-testid={`member-remove-${member.id}`}
                        onClick={() => remove(member)}
                        className="p-1.5 text-slate-500 hover:text-red-400 hover:bg-red-500/10 rounded-md transition"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </main>

      <Dialog open={open} onOpenChange={(value) => !value && setOpen(false)}>
        <DialogContent data-testid="add-member-dialog" className="bg-slate-900 border-slate-800 text-slate-100">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "Outfit" }}>Novo Membro</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3 mt-2">
            <div className="col-span-2">
              <Label className="text-xs text-slate-400">Nome</Label>
              <Input data-testid="member-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-slate-800 border-slate-700 mt-1" />
            </div>
            <div>
              <Label className="text-xs text-slate-400">Email</Label>
              <Input data-testid="member-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="bg-slate-800 border-slate-700 mt-1" />
            </div>
            <div>
              <Label className="text-xs text-slate-400">Senha</Label>
              <Input data-testid="member-password" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="bg-slate-800 border-slate-700 mt-1" />
            </div>
            <div className="col-span-2">
              <Label className="text-xs text-slate-400">Função</Label>
              <Select value={form.role} onValueChange={(value) => setForm({ ...form, role: value })}>
                <SelectTrigger className="bg-slate-800 border-slate-700 mt-1"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-700 text-slate-100">
                  <SelectItem value="agent">Agente de Vendas</SelectItem>
                  <SelectItem value="admin">Administrador</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="ghost" onClick={() => setOpen(false)} className="text-slate-300 hover:bg-slate-800">Cancelar</Button>
            <Button data-testid="member-submit" onClick={create} disabled={busy} className="bg-[#25D366] hover:bg-[#1EAC52] text-slate-950 font-semibold">
              Adicionar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={editOpen} onOpenChange={(value) => !value && setEditOpen(false)}>
        <DialogContent className="bg-slate-900 border-slate-800 text-slate-100">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "Outfit" }}>Editar membro</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3 mt-2">
            <div className="col-span-2">
              <Label className="text-xs text-slate-400">Nome</Label>
              <Input
                value={editForm.name}
                onChange={(e) => setEditForm((prev) => ({ ...prev, name: e.target.value }))}
                className="bg-slate-800 border-slate-700 mt-1"
              />
            </div>
            <div className="col-span-2">
              <Label className="text-xs text-slate-400">Email</Label>
              <Input
                type="email"
                value={editForm.email}
                onChange={(e) => setEditForm((prev) => ({ ...prev, email: e.target.value }))}
                className="bg-slate-800 border-slate-700 mt-1"
              />
            </div>
            <div className="col-span-2">
              <Label className="text-xs text-slate-400">Cargo</Label>
              <Select value={editForm.role} onValueChange={(value) => setEditForm((prev) => ({ ...prev, role: value }))}>
                <SelectTrigger className="bg-slate-800 border-slate-700 mt-1"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-700 text-slate-100">
                  {editForm.role === "owner" && <SelectItem value="owner" disabled>Dono</SelectItem>}
                  {editForm.role === "super_admin" && <SelectItem value="super_admin" disabled>Super Admin</SelectItem>}
                  <SelectItem value="agent">Agente</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                </SelectContent>
              </Select>
              {editingMember?.id === currentUserId && editForm.role === "agent" && (
                <p className="text-[11px] text-amber-300 mt-2">
                  Você não pode remover seus próprios privilégios de Admin.
                </p>
              )}
            </div>
            <div className="col-span-2 border-t border-slate-800 pt-3 mt-1">
              <Label className="text-xs text-slate-400">Senha temporária</Label>
              <div className="flex items-center gap-2 mt-1">
                <Input
                  type="text"
                  value={temporaryPassword}
                  onChange={(e) => setTemporaryPassword(e.target.value)}
                  placeholder="Deixe em branco para gerar automática"
                  className="bg-slate-800 border-slate-700"
                />
                <Button
                  type="button"
                  onClick={resetPassword}
                  disabled={resetBusy}
                  className="bg-amber-400 hover:bg-amber-300 text-slate-950 font-semibold"
                >
                  {resetBusy ? "Resetando..." : "Resetar Senha"}
                </Button>
              </div>
              <p className="text-[11px] text-slate-500 mt-2">
                Se o campo ficar vazio, o sistema gera uma senha temporária automaticamente.
              </p>
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="ghost" onClick={() => setEditOpen(false)} className="text-slate-300 hover:bg-slate-800">
              Cancelar
            </Button>
            <Button
              onClick={saveEdit}
              disabled={editBusy}
              className="bg-[#25D366] hover:bg-[#1EAC52] text-slate-950 font-semibold"
            >
              {editBusy ? "Salvando..." : "Salvar alterações"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
